import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { pdf } from "@react-pdf/renderer";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore, useOrganizationStore } from "../../../../store/organization";
import { eventStore, useEventStore } from "../../../../store/event";
import { SecondaryButton } from "../../../../components/Button";
import { GET_CARD_BRANDS, GET_FINANCIAL_DAILY, GET_PRODUCT_MIX, GET_VENDOR } from "./queries";
import { processData } from "./helpers";
import { FinancialRow, ProductMixRow, TransactionCardRow, VerticalReport } from "./types";
import { SummaryStats } from "./SummaryStats";
import { PaymentBreakdown } from "./PaymentBreakdown";
import { CardBrands } from "./CardBrands";
import { ProductMix } from "./ProductMix";
import { VerticalSalesPDF } from "./VerticalSalesPDF";
import { ReportFilters } from "../../components/ReportFilters";

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;
const { getState: getEventState } = eventStore;

// Vertical Sales Report — vendor-scoped sales statement for a single event.
// Use case: a vendor takes this to the event organizer to show what they delivered.
// Required filters: organization, event, AND a single vendor.
export const VerticalSalesReport = () => {
  const location = useLocation();
  const currentOrgId = useOrganizationStore((s) => s.organizationId);
  const currentEventId = useEventStore((s) => s.eventId);

  const [report, setReport] = useState<VerticalReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // ReportFilters writes the vendor multi-select as `vendor_id._in: [...]`. This report is
  // strictly per-vendor, so we require exactly one. (We also accept `_eq` defensively in case
  // the filter shape changes.)
  const search = new URLSearchParams(location.search);
  const whereParam = search.get("where");
  const parsedWhere: any = whereParam ? safeParse(whereParam) : {};
  const vendorIds: number[] = Array.isArray(parsedWhere?.vendor_id?._in)
    ? parsedWhere.vendor_id._in.map((v: any) => Number(v)).filter((n: number) => Number.isFinite(n))
    : parsedWhere?.vendor_id?._eq != null
      ? [Number(parsedWhere.vendor_id._eq)]
      : [];
  const vendorIdFromQuery = vendorIds.length === 1 ? vendorIds[0] : null;
  const tooManyVendors = vendorIds.length > 1;

  const canGenerate = !!currentOrgId && !!currentEventId && !!vendorIdFromQuery;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { eventId } = getEventState();
      const organizationId = getOrgState().organizationId;
      const where: any = whereParam ? JSON.parse(whereParam) : {};

      if (organizationId) where.organization_id = { _eq: Number(organizationId) };
      if (eventId)        where.event_id        = { _eq: eventId };

      const vendorId =
        (Array.isArray(where?.vendor_id?._in) && where.vendor_id._in.length === 1
          ? Number(where.vendor_id._in[0])
          : Number(where?.vendor_id?._eq)) || 0;
      if (!vendorId) {
        setError("Pick exactly one vendor — this report is vendor-scoped.");
        setIsLoading(false);
        return;
      }

      const [financialRes, productRes, cardRes, vendorRes] = await Promise.all([
        client.query({ query: GET_FINANCIAL_DAILY, variables: { where }, fetchPolicy: "network-only" }),
        client.query({ query: GET_PRODUCT_MIX,    variables: { where }, fetchPolicy: "network-only" }),
        client.query({ query: GET_CARD_BRANDS,    variables: { where }, fetchPolicy: "network-only" }),
        client.query({ query: GET_VENDOR,         variables: { id: vendorId }, fetchPolicy: "cache-first" }),
      ]);

      const financialRows: FinancialRow[]        = financialRes.data?.reports_financial    || [];
      const productRows:   ProductMixRow[]       = productRes.data?.reports_product_mix    || [];
      const cardRows:      TransactionCardRow[]  = cardRes.data?.reports_transactions      || [];
      const vendor                                = vendorRes.data?.vendors_by_pk;

      if (!vendor) {
        setError("Vendor not found.");
        setIsLoading(false);
        return;
      }

      const processed = processData(financialRows, productRows, cardRows, {
        name: vendor.name,
        revenue_share: vendor.revenue_share,
        flat_fee: vendor.flat_fee,
      });

      if (!mountedRef.current) return;
      setReport(processed);
      setHasGenerated(true);
    } catch (err: any) {
      console.error("Error fetching vertical sales report:", err);
      if (!mountedRef.current) return;
      setError(err.message || "Failed to load data");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [whereParam]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Filters + Generate (vendor filter and Generate share a row) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 pb-3 mb-4">
        <div className="flex items-end justify-between gap-3">
          <div className="flex-1">
            <ReportFilters
              includeVendors
              eventScopedVendors
              includeDates
              includeDateOnly
              includeDatesLast90Days
              customQueryPaths={{
                dateFrom: "_and[0].transaction_date",
                dateTo: "_and[1].transaction_date",
              }}
            />
          </div>
          <div className="flex items-center gap-2 mb-4">
            {hasGenerated && report && (
              <SecondaryButton onClick={() => exportPdf(report)}>
                Export PDF
              </SecondaryButton>
            )}
            <button
              onClick={fetchData}
              disabled={!canGenerate || isLoading}
              className={`inline-flex items-center justify-center border border-transparent px-4 py-2 text-sm font-semibold rounded-md shadow-sm transition-colors whitespace-nowrap ${
                canGenerate && !isLoading
                  ? "bg-red-700 text-white hover:bg-red-800"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </div>
        {(!canGenerate || tooManyVendors) && (
          <div className="pt-1">
            {!canGenerate && !tooManyVendors && (
              <span className="text-xs text-gray-400">
                Pick an organization, event, and vendor to generate the report.
              </span>
            )}
            {tooManyVendors && (
              <span className="text-xs text-amber-700">
                This report is vendor-scoped — please pick exactly one vendor.
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-red-700" />
        </div>
      )}

      {!isLoading && !hasGenerated && !error && (
        <div className="text-center py-20 text-gray-400">
          Pick an organization, event, and vendor — then click Generate Report.
        </div>
      )}

      {hasGenerated && report && (
        <>
          <div className="mb-3">
            <h1 className="text-xl font-semibold text-gray-900">{report.vendorName}</h1>
            <p className="text-sm text-gray-500">{report.eventName}</p>
          </div>
          <SummaryStats report={report} />
          <PaymentBreakdown rows={report.payments} />
          <CardBrands rows={report.cardBrands} />
          <ProductMix rows={report.products} />
        </>
      )}
    </div>
  );
};

function safeParse(s: string): any {
  try { return JSON.parse(s); } catch { return {}; }
}

async function exportPdf(report: VerticalReport) {
  const doc = <VerticalSalesPDF report={report} />;
  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
  const safeName = `${report.vendorName}_${report.eventName}`.replace(/[^a-z0-9]+/gi, "_");
  a.download = `Vertical_Sales_${safeName}_${timestamp}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
